import { useEffect, useRef, useState } from 'react'

// Moon — the canonical moon glyph, used by the bottom nav's Tonight tab AND
// WatchPlanButton (TonightMode). Redrawn 2026-08-06 as a filled disc with
// color-mix facet shading — same "Solid Facet" language as NavIcons.jsx and
// the FilmReel/Clapboard glyphs — replacing the earlier stroke crescent
// (a circle with a masked-out cutout) that read as a capital "C" at the small
// sizes this actually renders at (20px nav, 17px WatchPlanButton). A full
// disc has no thin cutout to lose, so it stays legible at both sizes.
//
// Optional `active` prop mirrors FilmReel's burst pattern: when it flips
// false -> true (a plan gets added), two rings bloom outward and the stars
// jitter to new positions. When `active` is omitted the icon renders static,
// so the nav caller (no "moment" to fire from) is unaffected.
export function MoonIcon({ size = 24, color = 'currentColor', active }) {
  const [burst, setBurst] = useState(false)
  const prev = useRef(active)
  const [jitter, setJitter] = useState({ s1: [0, 0], s2: [0, 0] })

  useEffect(() => {
    if (active && !prev.current) {
      setJitter({
        s1: [(Math.random() * 5.6 - 2.8).toFixed(2), (Math.random() * 5.6 - 2.8).toFixed(2)],
        s2: [(Math.random() * 5.6 - 2.8).toFixed(2), (Math.random() * 5.6 - 2.8).toFixed(2)],
      })
      setBurst(true)
      const t = setTimeout(() => setBurst(false), 700)
      prev.current = active
      return () => clearTimeout(t)
    }
    prev.current = active
  }, [active])

  // moonRing/moonPop/moonStar keyframes live in index.css, not an inline <style>
  // here — a <style> child of <svg> isn't reliably parsed as a stylesheet on
  // every browser, and on the ones where it isn't, its source text renders as
  // literal visible SVG text instead (found live via friction telemetry).
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ overflow: 'visible', '--ic': color }}>
      {burst && (
        <>
          <circle cx="12" cy="12" r="9" fill="none" stroke={color} strokeWidth="1.5" opacity="0"
            style={{ transformBox: 'view-box', transformOrigin: '12px 12px', '--nxa': 'moonRing 0.7s ease-out both' }} />
          <circle cx="12" cy="12" r="9" fill="none" stroke={color} strokeWidth="1.5" opacity="0"
            style={{ transformBox: 'view-box', transformOrigin: '12px 12px', '--nxa': 'moonRing 0.7s ease-out 0.12s both' }} />
        </>
      )}
      <g style={{
        transformBox: 'view-box', transformOrigin: '12px 12px',
        '--nxa': burst ? 'moonPop 0.55s var(--ease-pop, cubic-bezier(0.34,1.56,0.64,1))' : 'none',
      }}>
        <circle cx="12" cy="12" r="8" fill="var(--ic)"/>
        <path d="M12 4 a8 8 0 0 1 0 16 a10 10 0 0 0 0 -16 Z" fill="color-mix(in srgb, var(--ic) 60%, black 40%)"/>
      </g>
      <circle cx="8.2" cy="7.6" r="1" fill={color} opacity="0.55"
        style={burst ? { '--tx': `${jitter.s1[0]}px`, '--ty': `${jitter.s1[1]}px`, '--nxa': 'moonStar 0.6s ease-in-out both' } : undefined} />
      <circle cx="18" cy="6" r="0.6" fill={color} opacity="0.4"
        style={burst ? { '--tx': `${jitter.s2[0]}px`, '--ty': `${jitter.s2[1]}px`, '--nxa': 'moonStar 0.6s ease-in-out both' } : undefined} />
    </svg>
  )
}
